import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStaticQuery, graphql } from "gatsby";
import {
  faArrowLeft,
  faChevronCircleDown,
  faChevronRight,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import Button from "./button";
import { checkDarkMode } from "../utils/helpers";

const PopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 999;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    right: 20px;
    width: ${({ $open }) => ($open ? "calc(100% - 40px);" : "0")};
  }
`;
const PopupContainer = styled.div`
  font-family: var(--font-paragraph);
  display: ${({ $open }) => ($open ? "flex" : "none")};
  height: ${({ $open }) => ($open ? "100%" : "0")};
  flex-direction: column;
  align-items: flex-end;
  border-radius: 1rem;
  transition-property: transform, height;
  transition-timing-function: cubic-bezier(0.75, 0, 0.25, 1);
  transition-duration: 0.4s;
  z-index: 999;
  transform: ${({ $open }) =>
    $open ? "translateY(0)" : "translateY(calc(100% + 150px))"};
  background-color: var(--color-body);
  padding: 20px;
  border: 1px solid #e8e8e8;
  max-width: 550px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    margin-left: 30px;
    width: 100%;
    h1 {
      font-size: 1.4rem;
    }
  }
`;
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-primary);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease-in-out;
  font-size: 2.4rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 60px;
    width: 60px;
    font-size: 2rem;
  }
  &:hover {
    transform: scale(1.1);
  }
`;
const Icon = styled(FontAwesomeIcon)`
  color: var(--color-white);
`;
const PopupTitle = styled.h4`
  padding-inline: 1rem;
  margin-top: 1rem;
  font-size: 1.8rem;
  font-weight: 600;
`;
const Content = styled.div`
  width: 100%;
  background-color: var(--color-white);
  padding-block: 1rem;
  border-radius: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    max-height: 350px;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;
const Question = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid var(--color-body);
  padding: 1rem;
  font-weight: 600;
  &:hover {
    background-color: var(--color-body);
    cursor: pointer;
  }
`;
const ArrowRight = styled(FontAwesomeIcon)`
  font-size: 1.5rem;
`;
const ArrowBack = styled(FontAwesomeIcon)`
  background-color: ${({ theme }) => checkDarkMode("#f6dbd3", theme)};
  font-size: 1.5rem;
  border-radius: 50%;
  padding: 0.5rem;
  margin-inline: 1rem;
  &:hover {
    cursor: pointer;
  }
`;
const StyledButton = styled(Button)`
  width: 250px;
  white-space: nowrap;
  margin: 1rem;
`;
const AnswerContainer = styled.div`
  height: 100%;
  width: 100%;
  background-color: var(--color-white);
  padding-top: 1rem;
  border-radius: 1rem;
  transform: translateX(${({ $open }) => ($open ? "0" : "100%")});
  transition: transform 0.4s cubic-bezier(0.75, 0, 0.25, 1);
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    max-height: 350px;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

const FaqPopup = () => {
  const {
    wp: { crbThemeOptions },
  } = useStaticQuery(graphql`
    query {
      wp {
        crbThemeOptions {
          crbPopupTitle
          crbPopupSubtitle
          crbPopupButtonText
          crbPopupButtonUrl
          crbPopupFaq {
            question
            answer
          }
        }
      }
    }
  `);

  const [open, setOpen] = useState(false);
  const [canClose, setCanClose] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const ref = useRef();
  const handleClickOutside = (e) => {
    if (canClose && !ref.current?.contains(e.target)) {
      setOpen(false);
      setSelectedAnswer(null);
    }
  };
  useEffect(() => {
    setTimeout(() => {
      setCanClose(open);
    }, 400);
  }, [open]);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [canClose]);
  const handleQuestionClick = (index, e) => {
    e.stopPropagation();
    setSelectedAnswer(index);
  };

  const handleBackButton = (e) => {
    e.stopPropagation();
    setSelectedAnswer(null);
  };

  return (
    <PopupWrapper $open={open}>
      <PopupContainer $open={open} ref={ref} className={open ? "slide-up" : ""}>
        {selectedAnswer !== null ? (
          <AnswerContainer $open={selectedAnswer !== null}>
            <ArrowBack
              icon={faArrowLeft}
              onClick={(e) => handleBackButton(e)}
            />
            <Content style={{ fontWeight: "600", paddingInline: "1rem" }}>
              {crbThemeOptions.crbPopupFaq[selectedAnswer].question}
            </Content>
            <Content
              style={{
                borderBottom: "1px solid var(--color-body)",
                paddingInline: "1rem",
              }}
              dangerouslySetInnerHTML={{
                __html: crbThemeOptions.crbPopupFaq[selectedAnswer].answer,
              }}
            ></Content>
            <div>
              <StyledButton
                label={crbThemeOptions.crbPopupButtonText}
                to={crbThemeOptions.crbPopupButtonUrl}
              />
            </div>
          </AnswerContainer>
        ) : (
          <Content>
            <PopupTitle>{crbThemeOptions.crbPopupTitle}</PopupTitle>
            <p style={{ paddingInline: "1rem" }}>
              {crbThemeOptions.crbPopupSubtitle}
            </p>
            {crbThemeOptions.crbPopupFaq.map((faq, i) => (
              <Question key={i} onClick={(e) => handleQuestionClick(i, e)}>
                {faq.question}
                <ArrowRight icon={faChevronRight} />
              </Question>
            ))}
            <StyledButton
              label={crbThemeOptions.crbPopupButtonText}
              to={crbThemeOptions.crbPopupButtonUrl}
            />
          </Content>
        )}
      </PopupContainer>
      <IconWrapper onClick={() => setOpen(!open)}>
        <Icon icon={open ? faChevronCircleDown : faQuestionCircle} />
      </IconWrapper>
    </PopupWrapper>
  );
};

FaqPopup.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  loadButtonLabel: PropTypes.string,
  maxColumns: PropTypes.number,
  maxRows: PropTypes.number,
  children: PropTypes.node,
};

export default FaqPopup;
